import React, { useEffect, useState } from 'react';
import Type from 'prop-types';
import { useToggle } from 'react-use';
import { isDate, isEmpty, isNil, noop } from 'lodash';
import { Button, Chip, FormControl, FormGroup, FormLabel } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import RangeCalendar from '../RangeCalendar/RangeCalendar';
import { fullDateFormat, sapFullDateFormat } from 'constants/dateFormats';
import { useTranslation } from 'hooks';
import {
  formatDate,
  getDateFnsLocale,
  getDateRangeFromNow,
  getPeriodNameByRange as getPeriodNameByRangeFunc,
} from 'utils/date';
import { getInitialLocale } from 'utils/i18n';
import styles from './DateRangePickerField.module.css';

const getInitialDateValue = (value) => {
  if (!Array.isArray(value)) {
    return [undefined, undefined];
  }
  if (isEmpty(value) || value.every(isNil)) {
    return [undefined, undefined];
  }

  return value.map((date) => (isDate(date) ? date : new Date(date)));
};

const convertToISOString = (period) => {
  return period.map((date) => formatDate(date, sapFullDateFormat));
};

const DateRangePickerField = (props) => {
  const {
    label,
    tags,
    field,
    form: { setFieldValue },
    onChange,
    disabled,
    getPeriodNameByRange,
  } = props;
  const rawValue = getInitialDateValue(field.value);
  const [activeTag, setActiveTag] = useState(() =>
    getPeriodNameByRange(rawValue),
  );
  const { t } = useTranslation();
  const locale = getInitialLocale();
  const [isDialogOpened, toggleDialog] = useToggle(false);

  useEffect(() => {
    if (isNil(field.value?.[0]) || isNil(field.value?.[1])) {
      setActiveTag(null);
    } else {
      const periodName = getPeriodNameByRange(getInitialDateValue(field.value));
      setActiveTag(
        tags.some((tag) => tag.value === periodName) ? periodName : 'custom',
      );
    }
  }, [field.value, getPeriodNameByRange, tags]);

  const handleAccept = (newRawValue) => {
    const hasFullDateRange = newRawValue.every(isDate);
    const [dateStart, dateEnd] = newRawValue;
    const newValue = hasFullDateRange
      ? [
          formatDate(dateStart, sapFullDateFormat),
          formatDate(dateEnd, sapFullDateFormat),
        ]
      : [];
    setFieldValue(field.name, newValue);

    if (onChange) {
      onChange(newRawValue);
    }

    toggleDialog(false);

    if (hasFullDateRange) {
      setActiveTag('custom');
    }
  };

  const handleClear = () => {
    setActiveTag(null);
    setFieldValue(field.name, []);
  };

  const handleTagClick = (tagId) => () => {
    setActiveTag(tagId);

    const { dateStart, dateEnd } = getDateRangeFromNow(tagId);
    const newPeriod = convertToISOString([dateStart, dateEnd]);

    setFieldValue(field.name, newPeriod);
  };

  const handleTagDelete = () => () => {
    setActiveTag(null);
    setFieldValue(field.name, []);
  };

  const getFormattedPeriod = (period) => {
    return period.map((date) => formatDate(date, fullDateFormat)).join(' - ');
  };

  const renderTag = (tag) => {
    const { label: labelProp, value: valueProp } = tag;
    const checked = activeTag === valueProp;
    const color = checked ? 'primary' : 'default';
    const newLabel = checked ? getFormattedPeriod(rawValue) : labelProp;

    const tagProps = {
      className: styles.tag,
      color,
      label: newLabel,
      onClick: handleTagClick(valueProp),
      onDelete: checked ? handleTagDelete(valueProp) : undefined,
      clickable: true,
    };

    return <Chip key={valueProp} value={valueProp} {...tagProps} />;
  };

  const renderTags = () => {
    if (tags.length === 0) {
      return null;
    }

    return <>{tags.map((tag) => renderTag(tag))}</>;
  };

  const renderPicker = () => {
    return (
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={getDateFnsLocale(locale)}
      >
        <div className={styles.dateRangePickerWrapper}>
          <RangeCalendar
            open={isDialogOpened}
            value={rawValue}
            onApply={handleAccept}
            onClear={handleClear}
            onClose={() => toggleDialog(false)}
            title={t('selectPeriod')}
            startLabel={t('start')}
            endLabel={t('end')}
            applyLabel={t('apply')}
            cancelLabel={t('cancel')}
            clearLabel={t('reset')}
          />
        </div>
      </LocalizationProvider>
    );
  };

  const renderCustomPeriod = () => {
    if (activeTag === 'custom') {
      const newLabel = getFormattedPeriod(rawValue);

      return (
        <Chip
          key="custom"
          color="primary"
          sx={{ width: 'fit-content' }}
          label={newLabel}
          value="custom"
          onClick={() => toggleDialog(true)}
          onDelete={handleTagDelete('custom')}
          disabled={disabled}
        />
      );
    }

    return (
      <Button color="primary" onClick={toggleDialog} disabled={disabled}>
        {t('selectPeriod')}
      </Button>
    );
  };

  return (
    <FormControl component="fieldset" margin="normal">
      <FormLabel
        sx={{
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          fontWeight: '500',
          marginBottom: '8px',
        }}
        component="div"
      >
        {label}
      </FormLabel>
      <FormGroup sx={{ marginLeft: '-11px', gap: '10px' }}>
        {renderTags()}
        {renderCustomPeriod()}

        {renderPicker()}
      </FormGroup>
    </FormControl>
  );
};

DateRangePickerField.propTypes = {
  label: Type.string,
  field: Type.shape({
    name: Type.string.isRequired,
    value: Type.arrayOf(Type.string),
    onBlur: Type.func.isRequired,
    onChange: Type.func.isRequired,
  }).isRequired,
  form: Type.shape({
    setFieldValue: Type.func.isRequired,
  }).isRequired,
  tags: Type.arrayOf(
    Type.shape({
      label: Type.string.isRequired,
      value: Type.string.isRequired,
    }),
  ),
  onChange: Type.func,
  disabled: Type.bool,
  getPeriodNameByRange: Type.func,
};

DateRangePickerField.defaultProps = {
  label: '',
  tags: [],
  onChange: noop,
  disabled: false,
  getPeriodNameByRange: getPeriodNameByRangeFunc,
};

export default DateRangePickerField;
